// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, type IntlShape } from 'react-intl';
import {
  type Location,
  type RouterHistory,
  withRouter,
} from 'react-router-dom';
import { AutoSizer, Column, Table, SortDirection } from 'react-virtualized';

import { bindActionCreatorsSafe } from 'util/safeDispatch';
import { getActivePanelId, getUsers } from 'state/selectors';
import {
  deleteUsers,
  getUsers as getUsersAction,
} from 'state/commands/userCommands';
import type { Dispatch, User } from 'state/types';
import localizedStrings from 'util/localizedStrings';
import {
  findUserIndex,
  isAdminUser,
  sortUsers,
  renderUserType,
} from 'util/userUtils';
import {
  computeNewSelection,
  getNewSelectionState,
  getSelectedIndices,
  reindexSelectedItems,
  type SelectionState,
  type SortDirectionType,
} from 'util/tableUtils';
import { EditUserForm } from './UserForm';
import { showDialogPanel } from 'state/commands/dialogPanelCommands';
import CreateUserDialog from './CreateUserDialog';
import DeleteUsersDialog from './DeleteUsersDialog';
import PageHeader from '../PageHeader';
import {
  CLEANUP_TOKEN,
  CREATE_USER_PANEL_ID,
  DELETE_USER_PANEL_ID,
} from 'util/constants';
import logger from 'portfolio-shared/util/logging';
import MultipleSelectionTable from 'view/App/MultipleSelectionTable';

type Props = {
  activePanelId: ?string,
  deleteUsers: typeof deleteUsers,
  getUsersAction: typeof getUsersAction,
  intl: IntlShape,
  history: RouterHistory,
  location: Location,
  showDialogPanel: typeof showDialogPanel,
  users: User[],
};

type State = {
  selectionState: SelectionState,
  sortBy: string,
  sortDirection: SortDirectionType,
  sortedUsers: User[],
};

const MULTIPLE = 'multiple';
const NONE = 'none';

class UsersPage extends Component<Props, State> {
  deleteDialogCallback: Function;
  handleKeyDown: Function;
  handleClickAdd: Function;
  handleClickDelete: Function;
  handleSelectionChanged: Function;
  tableRef: ?typeof Table;
  sort: Function;
  userTypeCellRenderer: Function;

  state = {
    selectionState: getNewSelectionState(),
    sortDirection: SortDirection.ASC,
    sortBy: 'name',
    sortedUsers: [],
  };

  constructor() {
    super();
    this.deleteDialogCallback = this.deleteDialogCallback.bind(this);
    this.handleClickAdd = this.handleClickAdd.bind(this);
    this.handleClickDelete = this.handleClickDelete.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleSelectionChanged = this.handleSelectionChanged.bind(this);
    this.sort = this.sort.bind(this);
    this.userTypeCellRenderer = this.userTypeCellRenderer.bind(this);
  }

  componentDidMount() {
    this.props.getUsersAction();
  }

  componentWillReceiveProps(nextProps) {
    const { selectionState, sortBy, sortDirection } = this.state;

    if (
      this.props.users !== nextProps.users ||
      (this.props.location.pathname !== nextProps.location.pathname &&
        !nextProps.location.pathname.endsWith(MULTIPLE) &&
        !nextProps.location.pathname.endsWith(NONE))
    ) {
      const sortedUsers = sortUsers(nextProps.users, sortBy, sortDirection);
      const selectedIndex =
        findUserIndex(
          this.userUuidFromPath(nextProps.location.pathname),
          sortedUsers
        ) || 0;
      this.setState({
        selectionState: computeNewSelection(
          selectedIndex,
          sortedUsers,
          selectionState,
          {}
        ),
        sortedUsers,
      });
    }
  }

  // TODO: There's a bug in React 16.3 regarding getDerivedStateFromProps().
  //  It's fixed in 16.4 but will change when getDerivedStateFromProps() is called.
  // Reenable and test after we upgrade to 16.4
  //
  // https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html
  //
  // static getDerivedStateFromProps(nextProps, prevState) {
  //   return {
  //     ...prevState,
  //     sortedUsers: sortUsers(
  //       nextProps.users,
  //       prevState.sortBy,
  //       prevState.sortDirection
  //     ),
  //   };
  // }

  handleSelectionChanged(selectionState: SelectionState) {
    this.setState({ selectionState });
    const selectedIndices = getSelectedIndices(selectionState);

    if (selectedIndices.length === 1) {
      const uuid = this.state.sortedUsers[selectedIndices[0]].uuid;
      if (uuid) {
        this.props.history.replace(`/users/${uuid}`);
      }
    } else if (selectedIndices.length > 1) {
      this.props.history.replace(`/users/${MULTIPLE}`);
    } else if (selectedIndices.length === 0) {
      this.props.history.replace(`/users/${NONE}`);
    }
  }

  handleClickAdd() {
    this.props.showDialogPanel(CREATE_USER_PANEL_ID);
  }

  handleClickDelete() {
    this.props.showDialogPanel(DELETE_USER_PANEL_ID);
  }

  async deleteDialogCallback(deletePrivateGalleries: boolean) {
    const { selectionState, sortedUsers: sortedUsersOriginal } = this.state;
    // Make a copy so we don't modify state directly
    const sortedUsers = [...sortedUsersOriginal];
    const targets = this.getUsersInSelection();
    if (targets.length) {
      const cleanupToken = deletePrivateGalleries
        ? CLEANUP_TOKEN.deletePrivateGalleries
        : CLEANUP_TOKEN.makePrivateGalleriesPublic;

      logger.debug('Delete users: ', targets, ' token: ', cleanupToken);
      await this.props.deleteUsers(targets, [cleanupToken]);

      let lowestSelectedIndex = sortedUsersOriginal.length;
      const selectedIndices = getSelectedIndices(selectionState);
      // Compute lowest selected index
      selectedIndices.forEach(u => {
        if (u < lowestSelectedIndex) {
          lowestSelectedIndex = u;
        }
      });

      const newSortedUsers = sortedUsers.filter(
        (u, idx) => !selectedIndices.includes(idx)
      );
      this.setState({
        selectionState: computeNewSelection(
          Math.min(lowestSelectedIndex, sortedUsers.length - 2),
          newSortedUsers,
          selectionState,
          {}
        ),
        sortedUsers: newSortedUsers,
      });
    }
  }

  userUuidFromPath(pathname: string) {
    return pathname.split('/')[2];
  }

  sort({ sortBy, sortDirection }) {
    const sortedUsers = sortUsers(this.props.users, sortBy, sortDirection);
    this.setState({
      selectionState: reindexSelectedItems(
        sortedUsers,
        this.state.selectionState
      ),
      sortBy,
      sortDirection,
      sortedUsers,
    });
  }

  userTypeCellRenderer({
    cellData,
    columnData,
    columnIndex,
    dataKey,
    isScrolling,
    rowData,
    rowIndex,
  }) {
    return renderUserType(rowData, rowIndex, this.state.selectionState);
  }

  handleKeyDown(e: SyntheticKeyboardEvent<>) {
    // Typeahead first character only
    const { selectionState, sortBy, sortedUsers } = this.state;
    const searchField = sortBy === 'longName' ? 'longName' : 'name';

    if (!e.metaKey && /[a-z,0-9]/i.test(e.key)) {
      const index = sortedUsers.findIndex(
        u =>
          u[searchField] !== undefined &&
          u[searchField].substr(0, 1).localeCompare(e.key, undefined, {
            usage: 'search',
            sensitivity: 'base',
          }) === 0
      );

      if (index !== -1) {
        this.setState({
          selectionState: computeNewSelection(
            index,
            sortedUsers,
            selectionState,
            {}
          ),
        });
        e.preventDefault();
      }
    }
  }

  getUsersInSelection() {
    const { selectionState, sortedUsers } = this.state;
    const selectedIndices = getSelectedIndices(selectionState);
    return selectedIndices.map(idx => sortedUsers[idx]);
  }

  isDeleteDisabled() {
    return (
      this.getUsersInSelection().findIndex(u => u && isAdminUser(u)) !== -1
    );
  }

  render() {
    const {
      activePanelId,
      intl: { formatMessage },
    } = this.props;
    const { selectionState, sortBy, sortDirection, sortedUsers } = this.state;
    const selectedUsers = getSelectedIndices(selectionState);

    return (
      <div className="page-contents" data-test-name="users-page">
        {activePanelId === CREATE_USER_PANEL_ID && <CreateUserDialog />}
        <DeleteUsersDialog callback={this.deleteDialogCallback} />
        <div className="page-contents columns is-gapless">
          <div className="column is-one-third page">
            <PageHeader
              deleteDisabled={this.isDeleteDisabled()}
              onClickAdd={this.handleClickAdd}
              onClickDelete={this.handleClickDelete}
              title={formatMessage(localizedStrings.NAV_USERS)}
            />
            <div
              className="page bordered-page-column"
              onKeyDown={this.handleKeyDown}
            >
              <AutoSizer>
                {({ height, width }) => (
                  <MultipleSelectionTable
                    headerHeight={40}
                    height={height}
                    onSelectionChanged={this.handleSelectionChanged}
                    rowHeight={50}
                    selectionState={this.state.selectionState}
                    sort={this.sort}
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    sortedItems={this.state.sortedUsers}
                    width={width}
                  >
                    <Column
                      cellRenderer={this.userTypeCellRenderer}
                      dataKey="fromLDAP"
                      width={35}
                    />
                    <Column
                      className="list-item"
                      dataKey="name"
                      label={formatMessage(localizedStrings.USER_ACCOUNT)}
                      width={200}
                    />
                    <Column
                      className="list-item"
                      dataKey="longName"
                      label={formatMessage(localizedStrings.FULL_NAME)}
                      width={300}
                    />
                  </MultipleSelectionTable>
                )}
              </AutoSizer>
            </div>
          </div>
          <div className="column page">
            <div className="page-header" />
            {selectedUsers.length === 0 && (
              <div className="full-page-message">
                <FormattedMessage
                  {...localizedStrings.USER_NO_USERS_SELECTED}
                />
              </div>
            )}
            {sortedUsers.length > 0 &&
              selectedUsers.length === 1 &&
              sortedUsers[selectedUsers[0]] && (
                <EditUserForm
                  successMessage={formatMessage(
                    localizedStrings.NOTIFICATION_SUCCESS_UPDATE
                  )}
                  user={sortedUsers[selectedUsers[0]]}
                />
              )}
            {selectedUsers.length > 1 && (
              <div className="full-page-message">
                <FormattedMessage
                  {...localizedStrings.USER_MULT_USERS_SELECTED}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

let usersPageWrapper = connect(
  state => ({
    activePanelId: getActivePanelId(state),
    users: getUsers(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsSafe({
      deleteUsers,
      getUsersAction,
      showDialogPanel,
    })
)(injectIntl(UsersPage));

export default withRouter(usersPageWrapper);



// WEBPACK FOOTER //
// ./src/view/Pages/UsersPage/UsersPage.js